import {
  useNodeTreeState,
  Node,
  NodeRow,
} from "@darksnow-ui/node-tree-headless";
import { ChevronRight, ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import { NodeWrapper } from "./NodeWrapper";

interface EnhancedNodeRow extends NodeRow {
  node: Node;
  selected: boolean;
  expanded: boolean;
  visible: boolean;
  isCursor?: boolean;
  isFocus?: boolean;
}

interface TreeNodeProps {
  row: EnhancedNodeRow;
  renderLabel?: (node: Node) => React.ReactNode;
  renderIcon?: (node: Node, isExpanded: boolean) => React.ReactNode;
  indentSize?: number;
}

interface TreeNodeProps {
  row: EnhancedNodeRow;
  renderLabel?: (node: Node) => React.ReactNode;
  resolveIcon?: (node: Node, isExpanded: boolean) => string;
  indentSize?: number;
}

export function TreeNode({
  row,
  renderLabel,
  resolveIcon,
  indentSize = 20,
}: TreeNodeProps) {
  const { node } = row;
  const isExpanded = useNodeTreeState((s) => s.expandedNodes.has(node.id));
  const isSelected = useNodeTreeState((s) => s.selectedNodes.has(node.id));
  const isCursor = useNodeTreeState((s) => s.cursorId === node.id);
  const isFocus = useNodeTreeState((s) => s.focusId === node.id);
  const isDragOver = useNodeTreeState((s) => s.dragOverId === node.id);
  const isCut = useNodeTreeState(
    (s) => s.clipboardOperation === "cut" && s.clipboardNodeIds?.has(node.id),
  );

  // Default icon resolver
  const defaultIconResolver = (node: Node, isExpanded: boolean) => {
    if (!node.expandable) {
      const ext = node.label.split(".").pop()?.toLowerCase();
      switch (ext) {
        case "ts":
          return "typescript";
        case "tsx":
          return "react_ts";
        case "js":
          return "javascript";
        case "jsx":
          return "react";
        case "json":
          return "json";
        case "md":
          return "markdown";
        default:
          return "file";
      }
    }
    return isExpanded ? "folder-open" : "folder";
  };

  const iconName = resolveIcon
    ? resolveIcon(node, isExpanded)
    : defaultIconResolver(node, isExpanded);

  return (
    <NodeWrapper
      id={node.id}
      className={clsx(
        "group flex items-center px-2 cursor-pointer relative",
        "hover:bg-vscode-list-hoverBackground",
        isSelected && "bg-vscode-list-activeSelectionBackground",
        isCursor && "outline outline-1 outline-vscode-focusBorder",
        isDragOver && "bg-vscode-list-dropBackground",
        node.expandable && "expandable",
      )}
    >
      {/* Indent spacer */}
      <div style={{ width: `${row.level * indentSize}px` }} />

      {/* Expander */}
      <div className="flex items-center justify-center w-5 h-5">
        {node.expandable ? (
          <span
            className={clsx(
              "flex items-center justify-center w-full h-full",
              "hover:bg-vscode-border/50 rounded transition-colors",
            )}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </span>
        ) : null}
      </div>

      {/* Icon usando classe CSS */}
      <div className={clsx("flex-shrink-0 mr-2", isCut && "opacity-50")}>
        <i className={`icon icon-${iconName}`} />
      </div>

      {/* Label */}
      <span
        className={clsx(
          "flex-1 text-sm truncate",
          isSelected
            ? "text-vscode-list-activeSelectionForeground"
            : "text-vscode-foreground",
          isCut && "opacity-50",
          // isFocus && "font-semibold",
        )}
        aria-selected={isSelected}
      >
        {renderLabel ? renderLabel(node) : node.label}
      </span>
    </NodeWrapper>
  );
}
